import { useTranslation } from 'react-i18next';
import { formatRelativeTime } from '@/shared/utils/date';
import { cn } from '@/shared/utils/cn';
import type { ApiNotification } from '@/shared/types/api/notifications';

type NotificationItemProps = {
  notification: ApiNotification;
  onSelect: (notification: ApiNotification) => void;
};

export const WidgetsNotificationsItem = ({ notification, onSelect }: NotificationItemProps) => {
  const { t, i18n } = useTranslation('notifications');
  const unread = !notification.is_read;

  const relative = formatRelativeTime(notification.created_at, i18n.resolvedLanguage ?? i18n.language);
  const relativeLabel =
    relative.kind === 'now'
      ? t('justNow')
      : relative.kind === 'date'
        ? relative.value
        : t('timeAgo', { value: relative.value });

  return (
    <button
      type="button"
      onClick={() => onSelect(notification)}
      className={cn(
        'flex w-full items-start gap-3 rounded-2xl border px-4 py-3 text-left transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40',
        unread
          ? 'border-brand-200 bg-brand-50 hover:bg-brand-100'
          : 'border-slate-200 bg-white hover:bg-slate-50',
      )}
    >
      <span
        aria-hidden
        className={cn(
          'mt-2 h-2 w-2 shrink-0 rounded-full',
          unread ? 'bg-brand-600' : 'bg-transparent',
        )}
      />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          {unread && (
            <span className="rounded-full bg-brand-600 px-2 py-0.5 text-[11px] font-semibold text-white">
              {t('unread')}
            </span>
          )}
          <span className="text-xs text-ink-muted">{relativeLabel}</span>
        </div>
        <div className="mt-1 flex items-start gap-3">
          {notification.image && (
            <a
              href={notification.image}
              target="_blank"
              rel="noreferrer"
              onClick={(event) => event.stopPropagation()}
              className="shrink-0"
            >
              <img
                src={notification.image}
                alt={notification.text || t('imageAlt')}
                className="h-12 w-12 rounded-lg border border-slate-200 object-cover"
              />
            </a>
          )}
          <p className="min-w-0 flex-1 whitespace-pre-line break-words text-sm text-ink">
            {notification.text}
          </p>
        </div>
      </div>
    </button>
  );
};
